import { useCallback, useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';

/* Keep a piece of view state in the query string.
 *
 * Which vault you opened, whether you were on the table or the map, which mesh
 * and lens were on, which segment you had drilled into: all of it lived in
 * component state, so a refresh dropped you back at the vault list and you had
 * to click your way in again. Anything worth landing back on after a refresh
 * belongs in the URL, which also makes the view linkable.
 *
 * Writes use `replace`, not `push`. These are view settings rather than
 * navigation steps, and pushing each toggle would bury the page you actually
 * arrived from under a stack of lens changes.
 */
export function useUrlState(key, defaultValue) {
  const [params, setParams] = useSearchParams();
  const value = params.get(key) ?? defaultValue;

  const set = useCallback((next) => {
    setParams((prev) => {
      const p = new URLSearchParams(prev);
      // Resolve updater functions against the CURRENT params rather than a
      // captured value, so two setters firing in one handler do not clobber
      // each other.
      const resolved = typeof next === 'function' ? next(p.get(key) ?? defaultValue) : next;
      if (resolved === null || resolved === undefined || resolved === defaultValue) p.delete(key);
      else p.set(key, String(resolved));
      return p;
    }, { replace: true });
  }, [key, defaultValue, setParams]);

  return [value, set];
}

/** Boolean flavour: present and "1" is true, absent is false. */
export function useUrlFlag(key) {
  const [raw, setRaw] = useUrlState(key, '');
  const value = raw === '1';
  const set = useCallback((next) => {
    setRaw((prev) => ((typeof next === 'function' ? next(prev === '1') : next) ? '1' : ''));
  }, [setRaw]);
  return [value, set];
}

/* Change several keys in ONE navigation.
 *
 * This is not a convenience. Two setters in the same handler each receive the
 * router's current params, which do not change until the navigation commits, so
 * the second call computes from params that still lack the first call's change
 * and overwrites it. Switching the mesh set `mesh` and then cleared `group`,
 * and the clear silently threw the mesh away, which looked exactly like a dead
 * toggle. Any handler touching more than one key must go through here.
 *
 * A value of null or undefined removes the key.
 */
export function useUrlPatch() {
  const [, setParams] = useSearchParams();
  return useCallback((updates) => {
    setParams((prev) => {
      const p = new URLSearchParams(prev);
      Object.entries(updates).forEach(([k, v]) => {
        if (v === null || v === undefined || v === '') p.delete(k);
        else p.set(k, String(v));
      });
      return p;
    }, { replace: true });
  }, [setParams]);
}

/** Clear several keys at once, for "leave this screen" transitions. */
export function useUrlReset() {
  const patch = useUrlPatch();
  return useCallback((keys) => {
    patch(Object.fromEntries(keys.map((k) => [k, null])));
  }, [patch]);
}

/** The current params as a plain object, for effects that depend on all of them. */
export function useUrlSnapshot() {
  const [params] = useSearchParams();
  return useMemo(() => Object.fromEntries(params.entries()), [params]);
}
